import React, { useEffect, useMemo, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useTranslation } from "react-i18next";

import "./Buscar.css";
import WithAuthorization from "../../../../app/src/Utils/WithAuthorization";
import { turnOffLoadingActionCreator } from "../../../../app/src/actions/commonActions";
import { getTranslatedName, generateSlug } from "../../../../app/src/Utils/utils";
import { WIDGET_ACTION_SCREEN } from "../../../../app/src/Utils/Constants";
import { BUSCAR_SORT, searchBuscar, isGroupedSearchResponse, isScreensKind } from "../../../../app/src/services/buscarService";
import ScreenCard from "../../../../app/src/Components/common/ScreenCard/ScreenCard";

import { useSearchUrlState, SORT_KEYS } from "./state/searchUrlState";
import { useFilterNodes } from "./state/useFilterNodes";
import { buildSectionSlugMap, mergeSectionSlugMaps, EMPTY_SECTION_SLUG_MAP } from "./state/sectionSlugMap";
import { useIsMobile } from "./state/useIsMobile";

import SearchInput from "./components/SearchInput";
import FilterButton from "./components/FilterButton";
import DateFilter from "./components/DateFilter";
import SortSelect from "./components/SortSelect";
import FilterChips from "./components/FilterChips";
import FilterModal from "./components/FilterModal";
import SectionRow from "./components/SectionRow";
import ResultCard from "./components/ResultCard";
import CardSkeleton from "./components/CardSkeleton";
import EmptyState from "./components/EmptyState";
import ScopeIndicator from "./components/ScopeIndicator";
import { formatDateDisplay } from "./components/DateFilter";

const SORT_MAP = { [SORT_KEYS.RECENT]: BUSCAR_SORT.RECENT, [SORT_KEYS.OLDEST]: BUSCAR_SORT.OLDEST, [SORT_KEYS.AZ]: BUSCAR_SORT.ALPHABETICAL };
const getSortApi = (sortKey) => SORT_MAP[sortKey] || BUSCAR_SORT.RECENT;

// dateFromWord/dateToWord vienen de t("search.buscar.dateFrom"/"dateTo"): no puede vivir fuera
// del componente porque t() es un hook.
const dateChipLabel = (from, to, dateFromWord, dateToWord) => {
  if (!from && !to) return null;
  if (from && to && from === to) return formatDateDisplay(from);
  if (from && to) return `${formatDateDisplay(from)} – ${formatDateDisplay(to)}`;
  if (from) return `${dateFromWord} ${formatDateDisplay(from)}`;
  return `${dateToWord} ${formatDateDisplay(to)}`;
};

const BuscarPage = () => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const interfazId = useSelector((state) => state.commonState.interfazId);
  // OTT-1966: qué campos pinta la tarjeta de resultado (category/date/subtitle), configurable
  // por interfaz (comentario de Diego Dozo, 2026-09-22). Se lee de /interfaz, igual que interfazId.
  const cardFields = useSelector((state) => state.commonState.cardFields);

  // Bug reportado: al recargar el navegador estando en /buscar, la página se quedaba tapada por
  // el loader GLOBAL de la app indefinidamente. `commonState.loading` arranca en `true` en cada
  // carga fresca (commonState.js) y ninguna otra página lo apaga hasta que el usuario navega a
  // otra sección (Home/Section sí lo hacen en su propio mount) y vuelve; BuscarPage nunca lo
  // apagaba porque tiene su propio esqueleto de carga (loading/showLoadingSkeleton) y no
  // necesita el overlay global, igual que el resto de páginas de ruta (HomeComponent.js,
  // SectionComponent.js, etc.) lo apagan en cuanto tienen algo que mostrar.
  useEffect(() => {
    dispatch(turnOffLoadingActionCreator());
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  const {
    roots,
    childrenByParent,
    loadChildren,
    resolveIds,
    resolveSlugs,
    hasFilters,
    loading: filterNodesLoading,
  } = useFilterNodes(interfazId);
  const {
    state: urlState,
    setText,
    setNodeSlugs,
    setDateRange,
    setSortKey,
    enterSection,
    exitSection,
    clearFilters,
    clearAll,
    pushState,
  } = useSearchUrlState();
  const isMobile = useIsMobile(768);

  const [response, setResponse] = useState(null);
  const [flatItems, setFlatItems] = useState([]);
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [error, setError] = useState(null);
  const [sectionSlugMap, setSectionSlugMap] = useState(EMPTY_SECTION_SLUG_MAP);
  const [currentSectionName, setCurrentSectionName] = useState("");
  const [modalOpen, setModalOpen] = useState(false);
  const [retryTick, setRetryTick] = useState(0);
  const [showSkeleton, setShowSkeleton] = useState(false);
  const sentinelRef = useRef(null);
  // Controlador de la petición "activa" (búsqueda principal o lazy-load de fetchNextPage), la
  // que sea más reciente. fetchNextPage no tenía forma de cancelarse: si el usuario cambiaba de
  // texto/filtro/fecha con un lote de scroll todavía en vuelo, esa respuesta vieja llegaba
  // después y se APPENDeaba sobre los resultados ya filtrados (contenido repetido/erróneo tras
  // buscar dentro de una sección). Antes de lanzar cualquier petición nueva (la principal o la
  // de scroll) se aborta lo que hubiera en vuelo, así solo puede "ganar" la última.
  const activeControllerRef = useRef(null);
  // Caché de "¿esta interfaz tiene secciones?" (agrupada vs. plana), por interfazId. El contrato
  // dice que esa forma la decide únicamente `sections` en la respuesta, nunca configuración propia
  // del front (buscarService.js) — es una propiedad de la interfaz, no de la búsqueda concreta, así
  // que una vez se descubre no hace falta volver a sondearla en cada cambio de texto/filtro/fecha.
  const interfaceKindRef = useRef({});

  // Scroll al entrar en /buscar desde fuera (p.ej. la lupa del header a media Home): el cambio
  // de ruta no resetea el scroll por sí solo (React Router v5 no lo hace), así que sin esto la
  // página aparecía con el scroll donde estuviera la página anterior.
  useEffect(() => {
    window.scrollTo(0, 0);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // "Ver más"/"‹ Volver" (enterSection/exitSection) cambian solo el querystring de la misma
  // ruta, así que BuscarPage no se remonta y el scroll tampoco se toca por sí solo: al entrar en
  // una sección se sube al principio (se está viendo contenido nuevo) y al volver se restaura el
  // scroll que había en la vista agrupada al pulsar "Ver más" (guardado en el onMore de más abajo),
  // no el que haya quedado dentro de la sección tras su propio scroll/lazy loading.
  const sectionReturnScrollRef = useRef(null);
  const wasInSectionRef = useRef(false);

  // Evita el flash de skeletons (y, peor, un salto directo a "sin resultados") en cada cambio
  // de texto/filtro/fecha: mientras haya una respuesta previa que enseñar, se sigue mostrando
  // esa (stale-while-revalidate) y el skeleton solo aparece si la carga tarda más de 200 ms.
  useEffect(() => {
    if (!loading) {
      setShowSkeleton(false);
      return undefined;
    }
    const timer = setTimeout(() => setShowSkeleton(true), 200);
    return () => clearTimeout(timer);
  }, [loading]);

  const nodeIdsKey = resolveIds(urlState.nodeSlugs).join(",");

  // Fase 3: carga inicial / cada vez que cambia texto, filtros, fecha, orden o sección
  // (contrato §0/§3). El debounce de texto ya ocurre dentro de SearchInput; aquí se reacciona
  // directamente a `urlState.text` sin un segundo debounce.
  useEffect(() => {
    // La URL puede traer `nodes=<slug1>,<slug2>` de entrada (enlace compartido, contrato §4), pero
    // `nodeIdsKey` solo puede resolver esos slugs a ids reales una vez `useFilterNodes` termina de
    // cargar el árbol (petición aparte, asíncrona). Sin esta espera, la primera búsqueda saldría
    // con `nodeIdsKey=""` (sin filtrar) y se corregiría con una segunda en cuanto lleguen los
    // nodos: un parpadeo de resultados sin filtrar más una petición de más, para nada distinto del
    // resultado final. Solo aplica cuando la URL realmente trae nodos; una búsqueda sin filtros no
    // tiene por qué esperar al árbol de nodos.
    if (urlState.nodeSlugs.length > 0 && filterNodesLoading) return undefined;

    activeControllerRef.current?.abort();
    let cancelled = false;
    const controller = new AbortController();
    activeControllerRef.current = controller;
    setLoading(true);
    setError(null);
    setPage(1);

    const nodeIds = nodeIdsKey ? nodeIdsKey.split(",").map(Number) : [];
    const sortApi = getSortApi(urlState.sortKey);
    const limitGrouped = isMobile ? 2 : 5;
    const limitFlat = isMobile ? 8 : 15;

    const handleError = (err) => {
      if (cancelled) return;
      console.error(err);
      setError({ status: err?.status });
      setLoading(false);
    };

    // Corrección Fase 5: una interfaz SIN secciones debe recibir limit=15/8&page=1 desde la
    // primera petición (contrato, pantallas 01/12), no el limit=5/2 de la vista agrupada —
    // si no, la primera página quedaría en 5 elementos y el lazy loading (que sí usa 15/8)
    // dejaría un hueco de resultados nunca pedidos. Como no se sabe de antemano si la interfaz
    // tiene secciones, se repite la consulta con el tamaño de lote real en cuanto la respuesta
    // confirma que es plana.
    const fetchFlatEntry = () =>
      searchBuscar(
        { text: urlState.text, nodes: nodeIds, from: urlState.from, to: urlState.to, sort: sortApi, limit: limitFlat, page: 1, interfazId },
        { signal: controller.signal },
      );

    (async () => {
      try {
        // "Conocido" (el slug existe en el mapa) es distinto de "tiene id numérico": una sección
        // sin nodo propio (id null, 8h/8i — bucket "Contenidos"/"Páginas" de una interfaz sin
        // secciones reales) sigue siendo una sección real por la que se puede entrar, solo que
        // sin `section=<id>` que enviar. Confundir "no tiene id" con "slug desconocido" (como
        // hacía `sectionId == null` antes) deja la navegación colgada en el bootstrap para
        // siempre — nunca llega a lanzar la petición real de la sección (8h).
        const isSlugKnown = (map) => Object.prototype.hasOwnProperty.call(map.slugToId, urlState.sectionSlug);
        let sectionKnown = !!urlState.sectionSlug && isSlugKnown(sectionSlugMap);
        let sectionId = sectionKnown ? sectionSlugMap.slugToId[urlState.sectionSlug] : undefined;

        // Deep link a una sección cuyo slug todavía no conocemos: primero se aprende el mapa de
        // secciones con la consulta de entrada (contrato §4: "si un slug no existe, se ignora").
        if (urlState.sectionSlug && !sectionKnown) {
          if (interfaceKindRef.current[interfazId] === "flat") {
            // Ya sabemos (de una búsqueda anterior) que esta interfaz no tiene secciones: el
            // parámetro `section` no aplica, se ignora sin repetir la sonda agrupada.
            const flatRes = await fetchFlatEntry();
            if (cancelled) return;
            setResponse(flatRes);
            setFlatItems(flatRes.data || []);
            setLoading(false);
            return;
          }
          const bootstrap = await searchBuscar(
            { text: urlState.text, nodes: nodeIds, from: urlState.from, to: urlState.to, sort: sortApi, limit: limitGrouped, interfazId },
            { signal: controller.signal },
          );
          if (cancelled) return;
          if (isGroupedSearchResponse(bootstrap)) {
            interfaceKindRef.current[interfazId] = "grouped";
            const map = buildSectionSlugMap(bootstrap.sections);
            setSectionSlugMap((prev) => mergeSectionSlugMaps(prev, map));
            sectionKnown = isSlugKnown(map);
            if (sectionKnown) {
              sectionId = map.slugToId[urlState.sectionSlug];
              // Por slug, no por id: con id null puede haber más de una sección sin nodo propio
              // ("Contenidos" y "Páginas" a la vez) y `find(s => s.id === null)` cogería la
              // primera que encontrara, no necesariamente la que se pidió.
              const matched = bootstrap.sections.find((s) => generateSlug(s.name || "") === urlState.sectionSlug);
              if (matched) setCurrentSectionName(getTranslatedName(matched));
            } else {
              setResponse(bootstrap);
              setLoading(false);
              return;
            }
          } else {
            // Interfaz sin secciones: el parámetro `section` no aplica, se ignora.
            interfaceKindRef.current[interfazId] = "flat";
            const flatRes = await fetchFlatEntry();
            if (cancelled) return;
            setResponse(flatRes);
            setFlatItems(flatRes.data || []);
            setLoading(false);
            return;
          }
        }

        // Si ya sabemos (de una búsqueda anterior de esta misma interfaz) que es plana, se pide
        // directamente con el tamaño de lote y `page` reales: sondear otra vez con limitGrouped
        // en cada cambio de texto/filtro/fecha solo repetiría, siempre con el mismo resultado, la
        // petición extra que la corrección de la Fase 5 ya resolvió para la carga inicial.
        const knownFlat = !sectionKnown && interfaceKindRef.current[interfazId] === "flat";
        const res = await searchBuscar(
          {
            text: urlState.text,
            nodes: nodeIds,
            from: urlState.from,
            to: urlState.to,
            sort: sortApi,
            section: sectionId,
            limit: sectionKnown || knownFlat ? limitFlat : limitGrouped,
            page: sectionKnown || knownFlat ? 1 : undefined,
            interfazId,
          },
          { signal: controller.signal },
        );
        if (cancelled) return;
        if (isGroupedSearchResponse(res)) {
          interfaceKindRef.current[interfazId] = "grouped";
          setSectionSlugMap((prev) => mergeSectionSlugMaps(prev, buildSectionSlugMap(res.sections)));
          setResponse(res);
          setFlatItems([]);
        } else if (!sectionKnown) {
          interfaceKindRef.current[interfazId] = "flat";
          if (knownFlat) {
            setResponse(res);
            setFlatItems(res.data || []);
          } else {
            // Primera vez que se descubre que esta interfaz es plana: la sonda de entrada se
            // hizo con el limit/page de la vista agrupada, hace falta repetirla con los reales.
            const flatRes = await fetchFlatEntry();
            if (cancelled) return;
            setResponse(flatRes);
            setFlatItems(flatRes.data || []);
          }
        } else {
          setResponse(res);
          setFlatItems(res.data || []);
        }
        setLoading(false);
      } catch (err) {
        if (err?.name !== "AbortError") handleError(err);
      }
    })();

    return () => {
      cancelled = true;
      controller.abort();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    interfazId,
    urlState.text,
    nodeIdsKey,
    urlState.from,
    urlState.to,
    urlState.sortKey,
    urlState.sectionSlug,
    isMobile,
    retryTick,
    filterNodesLoading,
  ]);

  const isGrouped = response ? isGroupedSearchResponse(response) : false;
  const filterCount = response?.meta?.filter_count ?? 0;
  const hasMore = !isGrouped && flatItems.length < filterCount;

  const fetchNextPage = async () => {
    if (loadingMore || !hasMore) return;
    activeControllerRef.current?.abort();
    const controller = new AbortController();
    activeControllerRef.current = controller;
    setLoadingMore(true);
    const nodeIds = nodeIdsKey ? nodeIdsKey.split(",").map(Number) : [];
    const sortApi = getSortApi(urlState.sortKey);
    const sectionId = urlState.sectionSlug ? sectionSlugMap.slugToId[urlState.sectionSlug] : undefined;
    const batchSize = isMobile ? 8 : 15;
    try {
      const nextPage = page + 1;
      const res = await searchBuscar(
        {
          text: urlState.text,
          nodes: nodeIds,
          from: urlState.from,
          to: urlState.to,
          sort: sortApi,
          section: sectionId,
          limit: batchSize,
          page: nextPage,
          interfazId,
        },
        { signal: controller.signal },
      );
      // Si mientras tanto se lanzó una búsqueda nueva (texto/filtro/fecha/sección), esta
      // respuesta ya no corresponde a lo que hay en pantalla: se descarta en vez de
      // añadirla, aunque el fetch no llegara a abortarse a tiempo.
      if (controller.signal.aborted) return;
      setFlatItems((prev) => [...prev, ...(res.data || [])]);
      setResponse(res);
      setPage(nextPage);
    } catch (err) {
      if (err?.name !== "AbortError") console.error(err);
    } finally {
      setLoadingMore(false);
    }
  };

  useEffect(() => {
    if (isGrouped || !hasMore || loading) return undefined;
    const node = sentinelRef.current;
    if (!node) return undefined;
    const observer = new IntersectionObserver(
      (entries) => entries[0]?.isIntersecting && fetchNextPage(),
      { rootMargin: "600px" },
    );
    observer.observe(node);
    return () => observer.disconnect();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isGrouped, hasMore, loading, loadingMore, page]);

  const allNodes = useMemo(() => [...roots, ...Object.values(childrenByParent).flat()], [roots, childrenByParent]);
  const nodeChips = urlState.nodeSlugs.map((slug) => {
    const id = resolveIds([slug])[0];
    const node = allNodes.find((n) => n.id === id);
    return node ? { key: slug, label: getTranslatedName(node), onRemove: () => setNodeSlugs(urlState.nodeSlugs.filter((s) => s !== slug)) } : null;
  }).filter(Boolean);
  const dateLabel = dateChipLabel(urlState.from, urlState.to, t("search.buscar.dateFrom"), t("search.buscar.dateTo"));
  const chips = dateLabel ? [...nodeChips, { key: "__date", label: dateLabel, onRemove: () => setDateRange("", "") }] : nodeChips;
  const activeCount = urlState.nodeSlugs.length + (urlState.from || urlState.to ? 1 : 0);

  // Solo se considera "dentro de una sección" si el slug de la URL se resolvió a una sección
  // conocida (con id numérico o sin él, 8h/8i): un slug desconocido se ignora (contrato §4) y
  // la página muestra la vista agrupada normal. `hasOwnProperty` y no `!= null`: una sección sin
  // nodo propio resuelve a id `null`, que es un valor conocido, no "no resuelto todavía".
  const inSection =
    !isGrouped && !!urlState.sectionSlug && Object.prototype.hasOwnProperty.call(sectionSlugMap.slugToId, urlState.sectionSlug);

  useEffect(() => {
    if (inSection && !wasInSectionRef.current) {
      window.scrollTo(0, 0);
    } else if (!inSection && wasInSectionRef.current) {
      window.scrollTo(0, sectionReturnScrollRef.current ?? 0);
      sectionReturnScrollRef.current = null;
    }
    wasInSectionRef.current = inSection;
  }, [inSection]);

  const isScreensView = !isGrouped && isScreensKind(response?.meta?.kind);
  const skeletonCount = inSection || !isGrouped ? (isMobile ? 8 : 15) : isMobile ? 2 : 5;
  const isEmpty = isGrouped ? (response?.sections?.length ?? 0) === 0 : filterCount === 0;
  // Sin respuesta previa (primera carga): no hay nada que mantener en pantalla mientras se
  // decide si hace falta skeleton, así que se muestra de inmediato en vez de esperar los 200 ms.
  const hasResponse = response !== null;
  const showLoadingSkeleton = loading && (showSkeleton || !hasResponse);
  const showStaleContent = !loading || (!showSkeleton && hasResponse);

  // "Páginas" (contrato §2.5). screensSection: la interfaz tiene la sección configurada (si no
  // viene nunca en ninguna respuesta, no se muestra ningún aviso en ningún sitio).
  // screensHiddenByFilters: hay página(s) que esta búsqueda escondería por tener filtros/fecha
  // activos ahora mismo — viene en meta tanto en la respuesta agrupada como en la de sección.
  const screensSection = !!response?.meta?.screensSection;
  const screensHiddenByFilters = !!response?.meta?.screensHiddenByFilters;

  return (
    <div className="bq-page">
      <div className="bq-controls">
        <SearchInput value={urlState.text} onChange={setText} />
        <div className="bq-controls-row">
          <div className="bq-controls-left">
            <FilterButton hasFilters={hasFilters} activeCount={activeCount} open={modalOpen} onClick={() => setModalOpen(true)} />
            {isMobile && <SortSelect value={urlState.sortKey} onChange={setSortKey} />}
          </div>
          <DateFilter from={urlState.from} to={urlState.to} onChange={setDateRange} mobile={isMobile} />
          {!isMobile && <SortSelect value={urlState.sortKey} onChange={setSortKey} />}
        </div>
        <FilterChips chips={chips} onClearAll={clearFilters} />
        {/* Fase 3, regla 1: nada de esto se ve si la interfaz no tiene la sección configurada
            (screensSection), aunque el back llegara a mandar screensHiddenByFilters solo. */}
        {screensSection && screensHiddenByFilters && <p className="bq-screens-note">{t("search.screens.hiddenByFilters")}</p>}
      </div>

      {inSection && <ScopeIndicator sectionName={currentSectionName} onBack={exitSection} />}

      {error && (
        <EmptyState
          variant={error.status === 400 ? "empty" : "error"}
          onClear={clearAll}
          onRetry={() => setRetryTick((prev) => prev + 1)}
        />
      )}

      {!error && showLoadingSkeleton && (
        <div className="bq-grid" aria-busy="true">
          {Array.from({ length: skeletonCount }).map((_, i) => (
            <CardSkeleton key={i} />
          ))}
        </div>
      )}

      {!error && showStaleContent && isEmpty && <EmptyState onClear={clearAll} />}

      {!error && showStaleContent && !isEmpty && isGrouped && (
        <>
          {urlState.text && (
            <div className="bq-results-title">
              <h2>{t("search.buscar.resultsForQuery", { query: urlState.text })}</h2>
              <span>{t("search.buscar.resultsCount", { count: response.meta.total })}</span>
            </div>
          )}
          {response.sections.map((s) => (
            <SectionRow
              key={s.id ?? s.name}
              title={getTranslatedName(s)}
              count={s.count}
              cards={s.data}
              kind={s.kind || "contents"}
              loading={false}
              hasSearched={!!urlState.text || activeCount > 0}
              cardFields={cardFields}
              // 8h/8i: `onMore` se pinta igual para todas las secciones, tengan o no id numérico
              // (bucket "Contenidos"/"Páginas" sin nodo propio, id null). Entrar sin id real
              // puede no dar una vista plana navegable (sin `section=<id>` que enviar, la API
              // puede seguir devolviendo la agrupada — ver el efecto de arriba), pero eso ya lo
              // resuelve la propia carga, no aquí: ocultar el botón de antemano solo escondía el
              // síntoma sin arreglar nada.
              onMore={() => {
                sectionReturnScrollRef.current = window.scrollY;
                setCurrentSectionName(getTranslatedName(s));
                setSectionSlugMap((prev) => mergeSectionSlugMaps(prev, buildSectionSlugMap([s])));
                enterSection(buildSectionSlugMap([s]).idToSlug[s.id]);
              }}
            />
          ))}
        </>
      )}

      {!error && showStaleContent && !isEmpty && !isGrouped && (
        <>
          {inSection ? (
            <div className="bq-results-title">
              <h2>{currentSectionName}</h2>
              <span>
                {urlState.text
                  ? t("search.buscar.resultsCountForQuery", { count: filterCount, query: urlState.text })
                  : t("search.buscar.resultsCount", { count: filterCount })}
              </span>
            </div>
          ) : (
            urlState.text && (
              <div className="bq-results-title">
                <h2>{t("search.buscar.resultsForQuery", { query: urlState.text })}</h2>
                <span>{t("search.buscar.resultsCount", { count: filterCount })}</span>
              </div>
            )
          )}
          {isScreensView ? (
            <div className="custom-grid-widget GroupWidget">
              <div className="grid-container" aria-busy={loadingMore}>
                {flatItems.map((item) => (
                  <ScreenCard key={item.id} item={item} trigger={WIDGET_ACTION_SCREEN} />
                ))}
                {loadingMore && Array.from({ length: isMobile ? 8 : 15 }).map((_, i) => <CardSkeleton key={`more-${i}`} />)}
              </div>
            </div>
          ) : (
            <div className="bq-grid" aria-busy={loadingMore}>
              {flatItems.map((c) => (
                <ResultCard key={c.id} content={c} cardFields={cardFields} />
              ))}
              {loadingMore && Array.from({ length: isMobile ? 8 : 15 }).map((_, i) => <CardSkeleton key={`more-${i}`} />)}
            </div>
          )}
          {hasMore && <div ref={sentinelRef} className="bq-sentinel" />}
        </>
      )}

      <FilterModal
        open={modalOpen}
        onClose={() => setModalOpen(false)}
        roots={roots}
        childrenByParent={childrenByParent}
        loadChildren={loadChildren}
        selectedIds={resolveIds(urlState.nodeSlugs)}
        dateLabel={dateLabel}
        onClearDate={() => setDateRange("", "")}
        onApply={(ids, { clearDate } = {}) => {
          // Un solo pushState para nodos + fecha: si se hiciera con setNodeSlugs y luego
          // setDateRange (o al revés) por separado, la segunda llamada partiría de un `state`
          // todavía sin el cambio de la primera y lo pisaría (ver comentario de `pushState`).
          pushState({ nodeSlugs: resolveSlugs(ids), ...(clearDate ? { from: "", to: "" } : {}) });
          setModalOpen(false);
        }}
      />
    </div>
  );
};

export default WithAuthorization(BuscarPage);
